/**
 * parseScreenplayText — Parse streaming screenplay text into scene cards.
 *
 * Converts the growing `out_script` text (standard screenplay format from
 * the streaming prompt) into an array of ScreenplayScene objects that the
 * ScriptWriterNode can render as progressive scene cards.
 *
 * Handles partial/incomplete text gracefully — the last scene may have
 * incomplete entries, which is expected during streaming.
 */

import { createDebug } from '@ponpon/api-client'

const log = createDebug('dw:script-streaming:parser')

export interface ScreenplayEntry {
  type: 'action' | 'dialogue' | 'os' | 'sfx' | 'music'
  character?: string
  emotion?: string
  text: string
}

export interface ScreenplayScene {
  scene_number: number
  location: string
  time_of_day?: string
  interior_exterior?: string
  characters?: string[]
  entries: ScreenplayEntry[]
}

// Scene heading pattern: INT./EXT./INT./EXT. LOCATION - TIME
const HEADING_RE = /^(INT\.\/EXT\.|INT\.|EXT\.)\s+(.+?)(?:\s*[-\u2013\u2014]\s*(.+))?$/

// Character cue: ALL CAPS name, optionally with (O.S.)
const CHAR_CUE_RE = /^([A-Z][A-Z\s.']+?)(?:\s*\(O\.S\.\))?\s*$/

// Parenthetical emotion
const PAREN_RE = /^\s*\((.+?)\)\s*$/

// SFX line
const SFX_RE = /^\[SFX\]\s*(.+)$/i

// Music line
const MUSIC_RE = /^\[MUSIC\]\s*(.+)$/i

/**
 * Parse screenplay text into an array of ScreenplayScene objects.
 *
 * The text follows standard screenplay format:
 * - Scene headings: INT./EXT. LOCATION - TIME
 * - Character cues: ALL CAPS name
 * - Parentheticals: (emotion)
 * - Dialogue: indented text after character cue
 * - [SFX] and [MUSIC] prefixed lines
 * - Action: everything else
 */
export function parseScreenplayText(text: string): ScreenplayScene[] {
  if (!text || !text.trim()) return []

  const lines = text.split('\n')
  const scenes: ScreenplayScene[] = []

  let currentScene: ScreenplayScene | null = null
  let i = 0

  while (i < lines.length) {
    const line = lines[i].trimEnd()
    const trimmed = line.trim()

    // Skip empty lines
    if (!trimmed) {
      i++
      continue
    }

    // Check for scene heading
    const headingMatch = trimmed.match(HEADING_RE)
    if (headingMatch) {
      // Save previous scene
      if (currentScene) {
        scenes.push(currentScene)
      }

      const intExt = headingMatch[1].replace(/\.$/, '')
      const location = headingMatch[2].trim()
      const timeOfDay = headingMatch[3]?.trim() || ''

      currentScene = {
        scene_number: scenes.length + 1,
        location,
        time_of_day: timeOfDay,
        interior_exterior: intExt,
        characters: [],
        entries: [],
      }
      i++
      continue
    }

    // If no scene heading yet, skip or create an implicit first scene
    if (!currentScene) {
      // Create an implicit scene for text before any heading
      currentScene = {
        scene_number: 1,
        location: 'Unknown',
        entries: [],
        characters: [],
      }
    }

    // Check for SFX
    const sfxMatch = trimmed.match(SFX_RE)
    if (sfxMatch) {
      currentScene.entries.push({ type: 'sfx', text: sfxMatch[1].trim() })
      i++
      continue
    }

    // Check for Music
    const musicMatch = trimmed.match(MUSIC_RE)
    if (musicMatch) {
      currentScene.entries.push({ type: 'music', text: musicMatch[1].trim() })
      i++
      continue
    }

    // Check for transition (CUT TO:, FADE OUT., etc.) — skip
    if (/^[A-Z\s]+[:.]\s*$/.test(trimmed) && trimmed.split(/\s+/).length <= 4) {
      i++
      continue
    }

    // Check for character cue
    const charMatch = trimmed.match(CHAR_CUE_RE)
    if (charMatch) {
      const charName = charMatch[1].trim()
      const isOS = trimmed.includes('(O.S.)')
      let emotion = ''
      let dialogueText = ''

      // Look ahead for parenthetical
      let j = i + 1
      if (j < lines.length) {
        const parenMatch = lines[j].match(PAREN_RE)
        if (parenMatch) {
          emotion = parenMatch[1].trim()
          j++
        }
      }

      // Collect dialogue lines
      const dialogueLines: string[] = []
      while (j < lines.length) {
        const dl = lines[j].trimEnd()
        if (!dl.trim()) break
        // Stop at next known element
        if (
          dl.trim().match(CHAR_CUE_RE) ||
          dl.trim().match(SFX_RE) ||
          dl.trim().match(MUSIC_RE) ||
          dl.trim().match(HEADING_RE)
        ) {
          break
        }
        dialogueLines.push(dl.trim())
        j++
      }

      dialogueText = dialogueLines.join(' ')

      if (dialogueText) {
        // Title-case the name
        const displayName = charName
          .split(/\s+/)
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(' ')

        currentScene.entries.push({
          type: isOS ? 'os' : 'dialogue',
          character: displayName,
          emotion,
          text: dialogueText,
        })

        // Track characters
        if (currentScene.characters && !currentScene.characters.includes(displayName)) {
          currentScene.characters.push(displayName)
        }
      }

      i = j
      continue
    }

    // Default: action line
    const actionLines: string[] = [trimmed]
    let j = i + 1
    while (j < lines.length) {
      const al = lines[j].trimEnd()
      if (!al.trim()) break
      if (
        al.trim().match(CHAR_CUE_RE) ||
        al.trim().match(SFX_RE) ||
        al.trim().match(MUSIC_RE) ||
        al.trim().match(HEADING_RE)
      ) {
        break
      }
      actionLines.push(al.trim())
      j++
    }

    currentScene.entries.push({ type: 'action', text: actionLines.join(' ') })
    i = j > i ? j : i + 1
  }

  // Push the last scene
  if (currentScene) {
    scenes.push(currentScene)
  }

  const totalEntries = scenes.reduce((sum, s) => sum + s.entries.length, 0)
  log('parsed textLen=%d scenes=%d entries=%d', text.length, scenes.length, totalEntries)

  return scenes
}
